import type { DiagnosisType, EnglishLevel } from "../types";
import { parseEnglishLevel } from "../level";

const BLOCK_TYPES: DiagnosisType[] = ["vocab", "phrase", "sentence", "logic", "background"];

export type ParsedDiagnosisBlock = {
  type: DiagnosisType;
  excerpt: string;
  translation?: string;
  note: string;
};

export type ParsedLearningStep = {
  title: string;
  content: string;
  checkQuestion: string;
};

export type ParsedDiagnosis = {
  summary: string;
  fullTranslation: string | null;
  suggestedTextLevel: EnglishLevel | null;
  suggestedLearnerLevel: EnglishLevel | null;
  learnerLevelReason: string | null;
  blocks: ParsedDiagnosisBlock[];
  steps: ParsedLearningStep[];
};

export type DiagnosisParseResult =
  | { ok: true; data: ParsedDiagnosis }
  | { ok: false; fallbackSummary: string; raw: string };

function extractJsonCandidate(raw: string): string {
  const trimmed = raw.trim();
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenced?.[1]) {
    return fenced[1].trim();
  }

  const start = trimmed.indexOf("{");
  const end = trimmed.lastIndexOf("}");
  if (start >= 0 && end > start) {
    return trimmed.slice(start, end + 1);
  }

  return trimmed;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function isBlockType(value: string): value is DiagnosisType {
  return BLOCK_TYPES.includes(value as DiagnosisType);
}

const TRANSLATION_REQUIRED_TYPES = new Set<DiagnosisType>(["vocab", "phrase", "sentence"]);

function parseBlocks(value: unknown): ParsedDiagnosisBlock[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const blocks: ParsedDiagnosisBlock[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const row = item as Record<string, unknown>;
    const type = asString(row.type);
    const excerpt = asString(row.excerpt);
    const translation = asString(row.translation);
    const note = asString(row.note);
    if (!isBlockType(type) || !excerpt || !note) {
      continue;
    }
    if (TRANSLATION_REQUIRED_TYPES.has(type) && !translation) {
      continue;
    }
    blocks.push({
      type,
      excerpt,
      ...(translation ? { translation } : {}),
      note,
    });
  }
  return blocks;
}

function parseSteps(value: unknown): ParsedLearningStep[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const steps: ParsedLearningStep[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const row = item as Record<string, unknown>;
    const title = asString(row.title);
    const content = asString(row.content);
    const checkQuestion = asString(row.checkQuestion);
    if (!title || !content) {
      continue;
    }
    steps.push({ title, content, checkQuestion });
  }
  return steps;
}

function extractPartialJsonString(aggregated: string, field: string): string | null {
  const match = aggregated.match(new RegExp(`"${field}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)`));
  if (!match?.[1]) {
    return null;
  }

  try {
    return JSON.parse(`"${match[1]}"`).trim() || null;
  } catch {
    const unescaped = match[1]
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\r")
      .replace(/\\t/g, "\t")
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, "\\");
    return unescaped.trim() || null;
  }
}

export const DIAGNOSIS_PROGRESS = {
  reading: "正在阅读文章…",
  summarizing: "正在归纳难点…",
  blocks: "正在标注卡点…",
  steps: "正在设计学习步骤…",
  finalizing: "正在整理结果…",
} as const;

/** Infer streaming diagnosis status from partial JSON content. */
export function extractDiagnosisProgressStatus(
  aggregated: string,
  options: { finalizing?: boolean } = {},
): string {
  if (options.finalizing) {
    return DIAGNOSIS_PROGRESS.finalizing;
  }

  const text = aggregated.trim();
  if (!text) {
    return DIAGNOSIS_PROGRESS.reading;
  }

  if (/"steps"\s*:\s*\[/.test(text)) {
    return DIAGNOSIS_PROGRESS.steps;
  }
  if (/"blocks"\s*:\s*\[/.test(text)) {
    return DIAGNOSIS_PROGRESS.blocks;
  }
  if (/"summary"\s*:\s*"/.test(text)) {
    return DIAGNOSIS_PROGRESS.summarizing;
  }

  return DIAGNOSIS_PROGRESS.reading;
}

/** Extract partial summary text from an in-progress JSON stream. */
export function extractPartialSummary(aggregated: string): string | null {
  return extractPartialJsonString(aggregated, "summary");
}

/** Extract partial fullTranslation text from an in-progress JSON stream. */
export function extractPartialFullTranslation(aggregated: string): string | null {
  return extractPartialJsonString(aggregated, "fullTranslation");
}

export function parseDiagnosisResponse(raw: string): DiagnosisParseResult {
  const candidate = extractJsonCandidate(raw);

  try {
    const json = JSON.parse(candidate) as Record<string, unknown>;
    const summary = asString(json.summary);
    const fullTranslation = asString(json.fullTranslation) || null;
    const blocks = parseBlocks(json.blocks);
    const steps = parseSteps(json.steps);
    const suggestedTextLevel = asString(json.suggestedTextLevel)
      ? parseEnglishLevel(json.suggestedTextLevel)
      : null;
    const suggestedLearnerLevel = asString(json.suggestedLearnerLevel)
      ? parseEnglishLevel(json.suggestedLearnerLevel)
      : null;
    const learnerLevelReason = asString(json.learnerLevelReason) || null;

    if (!summary) {
      return { ok: false, fallbackSummary: raw.trim(), raw };
    }

    return {
      ok: true,
      data: {
        summary,
        fullTranslation,
        suggestedTextLevel,
        suggestedLearnerLevel,
        learnerLevelReason,
        blocks,
        steps,
      },
    };
  } catch {
    const fallback = raw.trim();
    return { ok: false, fallbackSummary: fallback || "诊断结果解析失败", raw };
  }
}
